// Growem — Registrar comida. Global bottom-sheet overlay for logging a meal: optional photo,
// free-text description, AI (with offline fallback) estimate → editable macros → save.

import { useEffect, useRef, useState } from 'react'
import { useUI } from '../state/ui'
import { useStore } from '../state/store'
import { analyzeMeal } from '../lib/llm'
import { supabase } from '../lib/supabase'
import type { MealEstimate } from '../types'

export default function MealSheet() {
  const { mealOpen, mealPrefill, closeMeal } = useUI()
  const { today, addMeal, user } = useStore()

  const [desc, setDesc] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [imgUrl, setImgUrl] = useState<string | null>(null)
  const [est, setEst] = useState<MealEstimate | null>(null)
  const [busy, setBusy] = useState(false)
  const [saving, setSaving] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  // Reset every time the sheet (re)opens.
  useEffect(() => {
    if (mealOpen) {
      setDesc(mealPrefill)
      setFile(null); setImgUrl(null); setEst(null)
      setBusy(false); setSaving(false)
    }
  }, [mealOpen, mealPrefill])

  if (!mealOpen) return null

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null
    setFile(f)
    if (!f) { setImgUrl(null); return }
    const reader = new FileReader()
    reader.onload = () => setImgUrl(typeof reader.result === 'string' ? reader.result : null)
    reader.readAsDataURL(f)
  }

  async function analyze() {
    setBusy(true)
    try {
      const r = await analyzeMeal(desc, imgUrl ?? undefined)
      setEst(r)
    } finally {
      setBusy(false)
    }
  }

  function patch(k: keyof MealEstimate, v: number) {
    setEst((e) => (e ? { ...e, [k]: v } : e))
  }

  async function save() {
    if (!est || saving) return
    setSaving(true)
    let photoPath: string | null = null
    if (file && user) {
      try {
        const path = `${user.id}/${crypto.randomUUID()}.jpg`
        const { error } = await supabase.storage
          .from('meal-photos')
          .upload(path, file, { contentType: file.type })
        if (!error) photoPath = path
      } catch { /* ignore upload error → save without photo */ }
    }
    await addMeal(
      today,
      {
        n: est.nombre, kcal: est.kcal, prot: est.prot, carbs: est.carbs, fat: est.fat,
        photo: photoPath ? 1 : 0, photoPath, source: est.source, note: est.note,
      },
      desc,
    )
    closeMeal()
  }

  const tileWrap: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 12,
  }
  const tile: React.CSSProperties = {
    background: 'var(--volt-fill-08)', border: '1px solid var(--volt-border-30)',
    borderRadius: 'var(--r-sm)', padding: '8px 6px', textAlign: 'center',
  }
  const tileInput: React.CSSProperties = {
    width: '100%', background: 'none', border: 0, borderRadius: 0, padding: 0, outline: 0,
    textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 17,
    color: 'var(--volt)',
  }
  const tileLbl: React.CSSProperties = {
    fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
    color: 'var(--ink-3)', marginTop: 3,
  }

  const macros: [keyof MealEstimate, string][] = [
    ['kcal', 'kcal'], ['prot', 'prot g'], ['carbs', 'carb g'], ['fat', 'grasa g'],
  ]

  return (
    <div className="backdrop" onClick={closeMeal}>
      <div
        className="sheet scrollY"
        style={{ maxHeight: '72vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h1" style={{ marginBottom: 14 }}>Registrar comida</div>

        {/* Photo dropzone */}
        <label
          style={{
            display: 'block', border: '1.5px dashed var(--line-16)', borderRadius: 14,
            padding: imgUrl ? 8 : 22, textAlign: 'center', cursor: 'pointer',
            background: 'var(--surface-2)', marginBottom: 12,
          }}
        >
          {imgUrl ? (
            <img
              src={imgUrl}
              alt="Foto de la comida"
              style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 10, display: 'block' }}
            />
          ) : (
            <span className="tip">📷 Toca para añadir foto</span>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onPick}
            style={{ display: 'none' }}
          />
        </label>

        {/* Description */}
        <textarea
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="Describe tu comida: 2 huevos, arepa, pollo…"
          rows={3}
        />

        {/* Analyze */}
        <button
          className="btn"
          onClick={analyze}
          disabled={busy || (!desc.trim() && !imgUrl)}
          style={{
            marginTop: 12, opacity: busy || (!desc.trim() && !imgUrl) ? 0.55 : 1,
          }}
        >
          {busy ? 'Analizando…' : 'Analizar con IA'}
        </button>

        {/* Estimate */}
        {est && (
          <>
            <input
              value={est.nombre}
              onChange={(e) => setEst((s) => (s ? { ...s, nombre: e.target.value } : s))}
              placeholder="Nombre"
              style={{ marginTop: 12, fontWeight: 600 }}
            />
            <div style={tileWrap}>
              {macros.map(([k, lbl]) => (
                <div key={k} style={tile}>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={est[k] as number}
                    onChange={(e) => patch(k, Math.max(0, Math.round(Number(e.target.value) || 0)))}
                    style={tileInput}
                  />
                  <div style={tileLbl}>{lbl}</div>
                </div>
              ))}
            </div>
            {est.note && <div className="note" style={{ marginTop: 10 }}>{est.note}</div>}
          </>
        )}

        {/* Save */}
        <button
          className="btn"
          onClick={save}
          disabled={!est || saving}
          style={{ marginTop: 14, opacity: !est || saving ? 0.55 : 1 }}
        >
          {saving ? 'Guardando…' : 'Guardar comida'}
        </button>
      </div>
    </div>
  )
}
